// src/mappers/metricConfig.js

import { evaluateProfileFreshness } from "../utils/profileFreshness";

const toNumberOrNull = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const fmt = (n) => (Number.isFinite(Number(n)) ? Number(n).toLocaleString("en-GB") : "—");
const fmtPct = (n) => (Number.isFinite(Number(n)) ? `${Number(n).toFixed(2)}%` : "—");

const pickFirstNumber = (obj, keys) => {
  for (const k of keys) {
    const v = toNumberOrNull(obj?.[k]);
    if (v !== null) return v;
  }
  return null;
};

// Resolve fallback chains iteratively so malformed or exhausted chains can
// never recurse forever. The root metric identity remains stable while the
// label, hint and getter may come from a fallback definition.
const MAX_FALLBACK_DEPTH = 8;

const resolveWithFallback = (uiModel, def) => {
  const rootKey = def.key;
  const rootLockedBy = def.lockedBy;
  const rootFormat = def.format;

  let candidate = def;
  let resolved = {
    ...def,
    fallback: undefined,
  };
  let depth = 0;
  const visited = new Set();

  while (candidate && depth < MAX_FALLBACK_DEPTH) {
    if (visited.has(candidate)) {
      break;
    }

    visited.add(candidate);

    resolved = {
      ...resolved,
      ...candidate,
      key: rootKey,
      lockedBy: rootLockedBy,
      format: rootFormat,
      fallback: undefined,
    };

    const raw = candidate.get?.(uiModel);
    if (raw !== null && raw !== undefined) {
      return { ...resolved, raw };
    }

    candidate = candidate.fallback;
    depth += 1;
  }

  return { ...resolved, raw: null };
};

export const PLATFORM_METRICS = {
  instagram: [
    {
      key: "followers",
      label: "Followers",
      format: fmt,
      get: (m) => toNumberOrNull(m.followers),
    },
    {
      key: "engagement",
      label: "Engagement",
      format: fmtPct,
      lockedBy: "lockEngagement",
      get: (m) =>
        pickFirstNumber(m, ["avg_engagement_rate_30d", "avg_engagement_rate", "engagement_rate"]),
    },
    {
      key: "views",
      label: "Reel views",
      format: fmt,
      lockedBy: "lockViews",
      hint: "Average reel views",
      get: (m) =>
        pickFirstNumber(m, ["avg_reel_views_30d", "avg_reel_views", "avg_reels_views_30d", "avg_reels_views"]),
      fallback: {
        label: "Avg views",
        hint: "Average views",
        get: (m) =>
          pickFirstNumber(m, [
            "avg_views_30d",
            "avg_views",
            "avg_video_views_30d",
            "avg_video_views",
            "avg_story_views_30d",
            "avg_story_views",
            "views_avg_30d",
          ]),
        fallback: {
          label: "Posts (30d)",
          hint: "Posts in last 30 days",
          get: (m) => pickFirstNumber(m, ["posts_last_30d", "postsLast30d"]),
          fallback: {
            label: "Posts (90d)",
            hint: "Posts in last 90 days",
            get: (m) => pickFirstNumber(m, ["posts_last_90d", "postsLast90d"]),
          },
        },
      },
    },
  ],

  youtube: [
    {
      key: "followers",
      label: "Subscribers",
      format: fmt,
      get: (m) => toNumberOrNull(m.followers ?? m.subscriberCount),
    },
    {
      key: "engagement",
      label: "Engagement",
      format: fmtPct,
      lockedBy: "lockEngagement",
      get: (m) =>
        pickFirstNumber(m, ["avg_engagement_rate_30d", "avg_engagement_rate", "engagement_rate"]),
    },
    {
      key: "views",
      label: "Avg views",
      format: fmt,
      lockedBy: "lockViews",
      hint: "Average video views (recent)",
      get: (m) =>
        pickFirstNumber(m, ["avg_video_views_30d", "avg_views_30d", "avg_views", "avg_views_per_video_30d"]),
      fallback: {
        label: "Total views",
        hint: "Channel lifetime views",
        get: (m) => pickFirstNumber(m, ["total_views", "viewCount", "views_total"]),
        fallback: {
          label: "Videos",
          hint: "Total uploaded videos",
          get: (m) => pickFirstNumber(m, ["videoCount", "videos", "media_count", "video_count"]),
        },
      },
    },
  ],

  default: [
    {
      key: "followers",
      label: "Followers",
      format: fmt,
      get: (m) => toNumberOrNull(m.followers),
    },
    {
      key: "engagement",
      label: "Engagement",
      format: fmtPct,
      lockedBy: "lockEngagement",
      get: (m) =>
        pickFirstNumber(m, ["avg_engagement_rate_30d", "avg_engagement_rate", "engagement_rate"]),
    },
    {
      key: "views",
      label: "Posts (30d)",
      format: fmt,
      lockedBy: "lockViews",
      hint: "Posts in last 30 days",
      get: (m) => pickFirstNumber(m, ["posts_last_30d", "postsLast30d"]),
      fallback: {
        label: "Posts (90d)",
        hint: "Posts in last 90 days",
        get: (m) => pickFirstNumber(m, ["posts_last_90d", "postsLast90d"]),
      },
    },
  ],
};

export function buildMetrics(
  uiModel,
  { lockViews = false, lockEngagement = false, enforceFreshness = true } = {}
) {
  const platform = String(uiModel?.platform || "").toLowerCase();
  const defs = PLATFORM_METRICS[platform] || PLATFORM_METRICS.default;
  const freshness = evaluateProfileFreshness(uiModel);

  const resolved = defs.map((def) => resolveWithFallback(uiModel, def));

  if (enforceFreshness && !freshness.canShowMetrics) {
    return resolved.map((def) => ({
      key: def.key,
      label: def.label,
      value: "—",
      raw: null,
      hint: freshness.label,
      locked: false,
      freshnessStatus: freshness.status,
    }));
  }

  return resolved.map((def) => {
    const locked =
      (def.lockedBy === "lockViews" && Boolean(lockViews)) ||
      (def.lockedBy === "lockEngagement" && Boolean(lockEngagement));

    if (def.raw !== null && def.raw !== undefined) {
      return {
        key: def.key,
        label: def.label,
        value: def.format ? def.format(def.raw) : String(def.raw),
        raw: def.raw,
        hint: def.hint || "",
        locked,
      };
    }

    return {
      key: def.key,
      label: def.label,
      value: "—",
      raw: null,
      hint: def.hint || "",
      locked,
    };
  });
}
